<template>
  <v-dialog v-model="internalIsVisible" max-width="600px">
    <v-card>
      <v-card-title>Agregar jugador manualmente</v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form>
          <v-combobox
            v-model="jugadorSelected"
            :items="jugadores"
            item-title="nick"
            item-value="idUsuario"
            label="Selecciona el jugador a añadir"
          ></v-combobox>
          <!-- En una liga cada jugador lleva un ejército toda la liga -->
          <v-autocomplete
            v-if="esLiga"
            v-model="ejercitoElegido"
            :items="ejercitos"
            item-title="name"
            item-value="name"
            label="Ejército que llevará en la liga"
          />
          <v-row justify="center" class="my-4 ga-5">
            <v-btn
              variant="tonal"
              color="primary"
              :disabled="esLiga && !ejercitoElegido"
              @click="addJugador"
              large
              >Agregar jugador al torneo</v-btn
            >
            <v-btn variant="tonal" color="secondary" @click="closeModal" large
              >Cancelar</v-btn
            >
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Modal de progreso circular -->
  <v-dialog v-model="isGenerating" hide-overlay persistent>
    <v-card class="progress-card">
      <v-progress-circular
        indeterminate
        color="primary"
        size="70"
      ></v-progress-circular>
    </v-card>
  </v-dialog>

  <!-- Modal response eliminar inscripcion -->
  <ModalSuccess
    :isVisible="showSuccessModal"
    message="Jugador añadido correctamente."
    @update:isVisible="showSuccessModal = $event"
  />

  <!-- Modal response si error -->
  <ModalError
    :isVisible="showErrorModal"
    message="No se ha podido añadir al jugador. Contacta con el administrador."
    @update:isVisible="showErrorModal = $event"
  />
</template>

<script setup lang="ts">
import { JugadorParaEmparejamiento } from "@/interfaces/Live";
import { TorneoGestionInfoDTO } from "@/interfaces/Torneo";
import { ref, computed, defineProps, defineEmits, watch, onMounted } from "vue";
import { appsettings } from "@/settings/appsettings";
import { esPorJornadas } from "@/Constant/FormatoTorneo";
import { getUsuariosNoInscritosTorneoAsync } from "@/services/UsuariosService";
import { UsuarioFastDTO } from "@/interfaces/Usuario";
import { CrearInscripcionDTO } from "@/interfaces/Inscripcion";
import { registrarInscripcion } from "@/services/InscripcionesService";
import ModalError from "../Commons/ModalError.vue";
import ModalSuccess from "../Commons/ModalSuccess.vue";

const props = defineProps<{
  isVisible: boolean;
  torneo: TorneoGestionInfoDTO;
}>();
const emit = defineEmits(["close", "confirm"]);

const internalIsVisible = ref(props.isVisible);
const showErrorModal = ref<boolean>(false);
const showSuccessModal = ref<boolean>(false);

watch(
  () => props.isVisible,
  (newValue) => {
    internalIsVisible.value = newValue;
  }
);

const isGenerating = ref<boolean>(false);
const jugadorSelected = ref<JugadorParaEmparejamiento>();
const jugadores = ref<UsuarioFastDTO[]>();
// Liga y Copa: hay que elegir ejército
const esLiga = computed(() => esPorJornadas(props.torneo?.torneo.formatoTorneo));
const ejercitos = [...appsettings.armies].sort((a, b) => a.name.localeCompare(b.name));
const ejercitoElegido = ref<string | null>(null);

const closeModal = () => {
  internalIsVisible.value = false;
  emit("close");
};

const addJugador = async () => {
  try {
    isGenerating.value = true;
    const idTorneo = props.torneo?.torneo.idTorneo;
    const idUsuario = jugadorSelected.value?.idUsuario;

    if (idTorneo === undefined || idUsuario === undefined) {
      throw new Error("El idTorneo o idUsuario no están definidos");
    }
    const addInscripcion: CrearInscripcionDTO = {
      idTorneo: idTorneo,
      idUsuario: idUsuario,
      ejercitoElegido: esLiga.value ? ejercitoElegido.value ?? undefined : undefined,
    };

    await registrarInscripcion(addInscripcion);
    showSuccessModal.value = true;
  } catch (error) {
    console.error(error);
    showErrorModal.value = true;
  } finally {
    isGenerating.value = false;
    emit("confirm");

    closeModal();
  }
};

onMounted(async () => {
  try {
    const responseJugadores = await getUsuariosNoInscritosTorneoAsync(
      props.torneo.torneo.idTorneo
    );
    jugadores.value = (responseJugadores?.data ?? []).sort(
      (a: { nick: string }, b: { nick: string }) => a.nick.localeCompare(b.nick)
    );
  } catch (error) {
    console.error("Error al obtener la información del torneo:", error);
    jugadores.value = [];
  }
});
</script>

<style scoped>
.progress-card {
  width: 200px;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
}
</style>
